import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import axios from 'axios';
import './Dashboard.css';
import { isAdmin } from '../../helpers';
import { renderNumber, renderQuota } from '../../helpers/render';
import { StudioHero, StudioPage } from '../../components/StudioPage';

const chartColors = {
  requests: '#27272a',
  quota: '#4f63a8',
  tokens: '#287d78',
};

const modelColors = [
  '#27272a',
  '#5267a8',
  '#348984',
  '#8b6f47',
  '#765d91',
  '#a05e67',
  '#5d7284',
  '#687f52',
  '#956d55',
  '#666a73',
];

const tooltipStyle = {
  background: 'rgba(255,255,255,0.96)',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: 12,
  boxShadow: '0 14px 38px rgba(0,0,0,0.12)',
  color: '#18181b',
  padding: '10px 12px',
  backdropFilter: 'blur(10px)',
};

const MetricIcon = ({ type }) => {
  if (type === 'quota') {
    return (
      <svg viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M4.5 7.5h15v10h-15z' />
        <path d='M7.5 7.5V5.8c0-.72.58-1.3 1.3-1.3h6.4c.72 0 1.3.58 1.3 1.3v1.7' />
        <path d='M8 12.5h3.5M15.5 11v3' />
      </svg>
    );
  }
  if (type === 'tokens') {
    return (
      <svg viewBox='0 0 24 24' aria-hidden='true'>
        <path d='m12 3.8 7.1 4.1v8.2L12 20.2l-7.1-4.1V7.9z' />
        <path d='m4.9 7.9 7.1 4.2 7.1-4.2M12 12.1v8.1' />
      </svg>
    );
  }
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true'>
      <path d='M4 17.5 8.2 13l3.1 2.8L18.8 7' />
      <path d='M14.8 7h4v4' />
      <path d='M4 20h16' />
    </svg>
  );
};

const RefreshIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M19.2 8.2A8 8 0 1 0 20 14' />
    <path d='M19.2 4.8v3.4h-3.4' />
  </svg>
);

const EmptyChartIcon = () => (
  <svg viewBox='0 0 64 64' aria-hidden='true'>
    <path d='M12 48V29M25 48V20M38 48V34M51 48V14' />
    <path d='M8 49.5h48' />
  </svg>
);

const TrendCard = ({
  title,
  eyebrow,
  value,
  type,
  color,
  dataKey,
  data,
  loading,
  tooltipLabel,
  tooltipFormatter,
  dateFormatter,
  delay,
}) => (
  <article
    className={`studio-surface dashboard-metric-card dashboard-delay-${delay}`}
  >
    <div className='dashboard-metric-head'>
      <div>
        <span className='dashboard-eyebrow'>{eyebrow}</span>
        <h2>{title}</h2>
      </div>
      <span className={`dashboard-metric-icon is-${type}`}>
        <MetricIcon type={type} />
      </span>
    </div>
    {loading ? (
      <>
        <span className='dashboard-skeleton dashboard-value-skeleton' />
        <span className='dashboard-skeleton dashboard-chart-skeleton' />
      </>
    ) : (
      <>
        <div className='dashboard-metric-value'>{value}</div>
        <div className='dashboard-mini-chart'>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart
              data={data}
              margin={{ top: 8, right: 5, bottom: 0, left: 5 }}
            >
              <defs>
                <linearGradient
                  id={`dashboard-gradient-${dataKey}`}
                  x1='0'
                  y1='0'
                  x2='1'
                  y2='0'
                >
                  <stop offset='0%' stopColor={color} stopOpacity={0.45} />
                  <stop offset='100%' stopColor={color} stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke='rgba(0,0,0,0.055)'
                strokeDasharray='3 5'
                vertical={false}
              />
              <XAxis dataKey='date' hide />
              <YAxis hide domain={['auto', 'auto']} />
              <Tooltip
                cursor={{ stroke: 'rgba(0,0,0,0.1)', strokeDasharray: '3 3' }}
                contentStyle={tooltipStyle}
                labelStyle={{ color: '#71717a', fontSize: 11, marginBottom: 4 }}
                itemStyle={{ color: '#18181b', fontSize: 12 }}
                formatter={(currentValue) => [
                  tooltipFormatter(currentValue),
                  tooltipLabel,
                ]}
                labelFormatter={dateFormatter}
              />
              <Line
                type='monotone'
                dataKey={dataKey}
                stroke={`url(#dashboard-gradient-${dataKey})`}
                strokeWidth={2.6}
                dot={false}
                activeDot={{
                  r: 4,
                  fill: color,
                  stroke: '#ffffff',
                  strokeWidth: 2,
                }}
                animationDuration={650}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </>
    )}
  </article>
);

const SectionHeader = ({ icon, title, subtitle, badge, actions }) => (
  <div className='dashboard-section-header'>
    <div className='dashboard-section-title-wrap'>
      <span className='dashboard-section-icon'>
        <MetricIcon type={icon} />
      </span>
      <div>
        <div className='dashboard-section-title-line'>
          <h2>{title}</h2>
          {badge && <span className='dashboard-section-badge'>{badge}</span>}
        </div>
        <p>{subtitle}</p>
      </div>
    </div>
    {actions && <div className='dashboard-section-actions'>{actions}</div>}
  </div>
);

const SegmentedControl = ({ value, options, onChange, label }) => (
  <div className='dashboard-segmented' role='group' aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type='button'
        className={value === option.value ? 'is-active' : ''}
        onClick={() => onChange(option.value)}
        aria-pressed={value === option.value}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const isAdminUser = isAdmin();
  const [topUsers, setTopUsers] = useState([]);
  const [topUsersPeriod, setTopUsersPeriod] = useState('day');
  const [topUsersMetric, setTopUsersMetric] = useState('requests');
  const [topUsersLoading, setTopUsersLoading] = useState(false);

  const locale = i18n.resolvedLanguage?.startsWith('zh') ? 'zh-CN' : 'en-US';

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get('/api/user/dashboard');
      if (response.data.success) {
        setData(response.data.data || []);
        setLastUpdated(new Date());
      } else {
        setData([]);
        setLoadError(true);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setData([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTopUsers = useCallback(async () => {
    if (!isAdminUser) return;
    setTopUsersLoading(true);
    try {
      const end = Math.floor(Date.now() / 1000);
      const start = end - 30 * 24 * 60 * 60;
      const response = await axios.get('/api/log/top_users', {
        params: {
          period: topUsersPeriod,
          start_timestamp: start,
          end_timestamp: end,
          limit: 10,
        },
      });
      setTopUsers(response.data.success ? response.data.data || [] : []);
    } catch (error) {
      console.error('Failed to fetch top users:', error);
      setTopUsers([]);
    } finally {
      setTopUsersLoading(false);
    }
  }, [isAdminUser, topUsersPeriod]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchTopUsers();
  }, [fetchTopUsers]);

  const summaryData = useMemo(() => {
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return data
      .filter((item) => item.Day === todayKey)
      .reduce(
        (summary, item) => ({
          todayRequests: summary.todayRequests + (item.RequestCount || 0),
          todayQuota: summary.todayQuota + (item.Quota || 0),
          todayTokens:
            summary.todayTokens +
            (item.PromptTokens || 0) +
            (item.CompletionTokens || 0),
        }),
        { todayRequests: 0, todayQuota: 0, todayTokens: 0 }
      );
  }, [data]);

  const dateKeys = useMemo(() => {
    const dates = [];
    const current = new Date();
    current.setHours(12, 0, 0, 0);
    for (let offset = 6; offset >= 0; offset -= 1) {
      const date = new Date(current);
      date.setDate(current.getDate() - offset);
      dates.push(
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          '0'
        )}-${String(date.getDate()).padStart(2, '0')}`
      );
    }
    return dates;
  }, []);

  const models = useMemo(
    () => [...new Set(data.map((item) => item.ModelName).filter(Boolean))],
    [data]
  );

  const timeSeriesData = useMemo(() => {
    const dailyData = Object.fromEntries(
      dateKeys.map((date) => [date, { date, requests: 0, quota: 0, tokens: 0 }])
    );
    data.forEach((item) => {
      if (!dailyData[item.Day]) return;
      dailyData[item.Day].requests += item.RequestCount || 0;
      dailyData[item.Day].quota += item.Quota || 0;
      dailyData[item.Day].tokens +=
        (item.PromptTokens || 0) + (item.CompletionTokens || 0);
    });
    return dateKeys.map((date) => dailyData[date]);
  }, [data, dateKeys]);

  const modelData = useMemo(() => {
    const dailyData = Object.fromEntries(
      dateKeys.map((date) => [
        date,
        {
          date,
          ...Object.fromEntries(models.map((model) => [model, 0])),
        },
      ])
    );
    data.forEach((item) => {
      if (!dailyData[item.Day] || !item.ModelName) return;
      dailyData[item.Day][item.ModelName] +=
        (item.PromptTokens || 0) + (item.CompletionTokens || 0);
    });
    return dateKeys.map((date) => dailyData[date]);
  }, [data, dateKeys, models]);

  const topUserData = useMemo(() => {
    const grouped = {};
    topUsers.forEach((item) => {
      const username = item.username || `#${item.user_id}`;
      if (!grouped[username])
        grouped[username] = { username, request_count: 0, tokens: 0 };
      grouped[username].request_count += item.request_count || 0;
      grouped[username].tokens += item.tokens || 0;
    });
    const key = topUsersMetric === 'tokens' ? 'tokens' : 'request_count';
    return Object.values(grouped)
      .sort((a, b) => b[key] - a[key])
      .slice(0, 10);
  }, [topUsers, topUsersMetric]);

  const formatDate = useCallback(
    (dateValue) =>
      new Date(`${dateValue}T12:00:00`).toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
      }),
    [locale]
  );

  const formatCompact = useCallback(
    (value) =>
      new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value || 0),
    [locale]
  );

  const formatLastUpdated = lastUpdated?.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const xAxisProps = {
    dataKey: 'date',
    axisLine: false,
    tickLine: false,
    tick: { fontSize: 11, fill: '#8b8b93' },
    tickFormatter: formatDate,
    interval: 'preserveStartEnd',
    minTickGap: 24,
  };

  return (
    <StudioPage className='studio-dashboard-page'>
      <main className='dashboard-page'>
        <StudioHero
          icon='chart bar'
          kicker={t('dashboard.kicker')}
          title={t('dashboard.title')}
          description={t('dashboard.subtitle')}
          className='dashboard-hero'
        >
          <div className='dashboard-hero-actions'>
            {formatLastUpdated && (
              <span className='dashboard-updated'>
                {t('dashboard.updated_at', { time: formatLastUpdated })}
              </span>
            )}
            <button
              type='button'
              className='dashboard-refresh'
              onClick={fetchDashboardData}
              disabled={loading}
            >
              <RefreshIcon />
              <span>{t('dashboard.refresh')}</span>
            </button>
          </div>
        </StudioHero>

        {loadError && (
          <div className='dashboard-error-banner'>
            <span>{t('dashboard.load_failed')}</span>
            <button type='button' onClick={fetchDashboardData}>
              {t('dashboard.retry')}
            </button>
          </div>
        )}

        <section
          className='dashboard-metric-grid'
          aria-label={t('dashboard.today_summary')}
        >
          <TrendCard
            title={t('dashboard.charts.requests.title')}
            eyebrow={t('dashboard.today')}
            value={formatCompact(summaryData.todayRequests)}
            type='requests'
            color={chartColors.requests}
            dataKey='requests'
            data={timeSeriesData}
            loading={loading}
            tooltipLabel={t('dashboard.charts.requests.tooltip')}
            tooltipFormatter={formatCompact}
            dateFormatter={formatDate}
            delay='1'
          />
          <TrendCard
            title={t('dashboard.charts.quota.title')}
            eyebrow={t('dashboard.today')}
            value={renderQuota(summaryData.todayQuota, t)}
            type='quota'
            color={chartColors.quota}
            dataKey='quota'
            data={timeSeriesData}
            loading={loading}
            tooltipLabel={t('dashboard.charts.quota.tooltip')}
            tooltipFormatter={(value) => renderQuota(value, t)}
            dateFormatter={formatDate}
            delay='2'
          />
          <TrendCard
            title={t('dashboard.charts.tokens.title')}
            eyebrow={t('dashboard.today')}
            value={formatCompact(summaryData.todayTokens)}
            type='tokens'
            color={chartColors.tokens}
            dataKey='tokens'
            data={timeSeriesData}
            loading={loading}
            tooltipLabel={t('dashboard.charts.tokens.tooltip')}
            tooltipFormatter={formatCompact}
            dateFormatter={formatDate}
            delay='3'
          />
        </section>

        <section className='studio-surface dashboard-chart-card dashboard-enter'>
          <SectionHeader
            icon='tokens'
            title={t('dashboard.statistics.title')}
            subtitle={t('dashboard.statistics.subtitle')}
            badge={t('dashboard.statistics.model_count', {
              count: models.length,
            })}
          />
          {loading ? (
            <div className='dashboard-large-skeleton dashboard-skeleton' />
          ) : models.length === 0 ? (
            <div className='dashboard-empty-state'>
              <span>
                <EmptyChartIcon />
              </span>
              <h3>{t('dashboard.empty_title')}</h3>
              <p>{t('dashboard.empty_description')}</p>
            </div>
          ) : (
            <>
              <div className='dashboard-model-chart'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart
                    data={modelData}
                    margin={{ top: 12, right: 8, bottom: 2, left: 0 }}
                  >
                    <CartesianGrid
                      stroke='rgba(0,0,0,0.06)'
                      strokeDasharray='3 5'
                      vertical={false}
                    />
                    <XAxis {...xAxisProps} />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#8b8b93' }}
                      tickFormatter={formatCompact}
                      width={44}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      labelStyle={{
                        color: '#71717a',
                        fontSize: 11,
                        marginBottom: 5,
                      }}
                      itemStyle={{ fontSize: 11 }}
                      labelFormatter={(label) =>
                        `${t(
                          'dashboard.statistics.tooltip.date'
                        )}: ${formatDate(label)}`
                      }
                      formatter={(value, name) => [formatCompact(value), name]}
                    />
                    {models.map((model, index) => (
                      <Bar
                        key={model}
                        dataKey={model}
                        stackId='models'
                        fill={modelColors[index % modelColors.length]}
                        name={model}
                        radius={index === models.length - 1 ? [5, 5, 0, 0] : 0}
                        animationDuration={700}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className='dashboard-model-legend'>
                {models.map((model, index) => (
                  <span key={model} title={model}>
                    <i
                      style={{
                        background: modelColors[index % modelColors.length],
                      }}
                    />
                    {model}
                  </span>
                ))}
              </div>
            </>
          )}
        </section>

        {isAdminUser && (
          <section className='studio-surface dashboard-chart-card dashboard-enter'>
            <SectionHeader
              icon='requests'
              title={t('dashboard.userStats.title')}
              subtitle={t('dashboard.userStats.subtitle')}
              actions={
                <div className='dashboard-filter-row'>
                  <SegmentedControl
                    label={t('dashboard.userStats.period_label')}
                    value={topUsersPeriod}
                    onChange={setTopUsersPeriod}
                    options={[
                      {
                        value: 'day',
                        label: t('dashboard.userStats.period.day'),
                      },
                      {
                        value: 'month',
                        label: t('dashboard.userStats.period.month'),
                      },
                    ]}
                  />
                  <SegmentedControl
                    label={t('dashboard.userStats.metric_label')}
                    value={topUsersMetric}
                    onChange={setTopUsersMetric}
                    options={[
                      {
                        value: 'requests',
                        label: t('dashboard.userStats.metric.requests'),
                      },
                      {
                        value: 'tokens',
                        label: t('dashboard.userStats.metric.tokens'),
                      },
                    ]}
                  />
                </div>
              }
            />
            {topUsersLoading ? (
              <div className='dashboard-ranking-skeleton dashboard-skeleton' />
            ) : topUserData.length === 0 ? (
              <div className='dashboard-empty-state is-compact'>
                <span>
                  <EmptyChartIcon />
                </span>
                <h3>{t('dashboard.userStats.no_data')}</h3>
              </div>
            ) : (
              <div className='dashboard-ranking-chart'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart
                    data={topUserData}
                    layout='vertical'
                    margin={{ top: 5, right: 24, bottom: 0, left: 10 }}
                  >
                    <defs>
                      <linearGradient
                        id='dashboard-ranking-gradient'
                        x1='0'
                        y1='0'
                        x2='1'
                        y2='0'
                      >
                        <stop offset='0%' stopColor='#3f3f46' />
                        <stop offset='100%' stopColor='#18181b' />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      stroke='rgba(0,0,0,0.055)'
                      strokeDasharray='3 5'
                      horizontal={false}
                    />
                    <XAxis
                      type='number'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#8b8b93' }}
                      tickFormatter={formatCompact}
                    />
                    <YAxis
                      type='category'
                      dataKey='username'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#3f3f46' }}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      labelStyle={{ color: '#71717a', fontSize: 11 }}
                      itemStyle={{ color: '#18181b', fontSize: 12 }}
                      formatter={(value) => [
                        renderNumber(value),
                        topUsersMetric === 'tokens'
                          ? t('dashboard.userStats.tooltip.tokens')
                          : t('dashboard.userStats.tooltip.requests'),
                      ]}
                    />
                    <Bar
                      dataKey={
                        topUsersMetric === 'tokens' ? 'tokens' : 'request_count'
                      }
                      fill='url(#dashboard-ranking-gradient)'
                      radius={[0, 7, 7, 0]}
                      maxBarSize={20}
                      animationDuration={650}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
        )}
      </main>
    </StudioPage>
  );
};

export default Dashboard;
